/**
 * Organization types and their compliance profiles (docs/Decisions.md D64).
 *
 * Every organization is one of three types, chosen first in the setup wizard:
 *
 * - community: education, videos, content, forums and events. Charged by
 *   transaction (the plan's rate). No donations and no investment services.
 * - nonprofit: the community tools plus donations to the nonprofit itself,
 *   with the guardrails charitable solicitation needs, California first: a
 *   verified profile (legal name, EIN, IRS status, state, the California
 *   Registry of Charities and Fundraisers number when soliciting there) and a
 *   donor disclosure shown on the donate page and every receipt. The org
 *   admin manages its own fee (D60) within the platform caps (D53).
 * - investment: the core tools for an investment community. The platform
 *   charges a flat fee per service and never a share of a transaction, so no
 *   percentage fee and no donations; a verified compliance profile records
 *   the entity, the offering exemption relied on and the adviser status, and
 *   unlocks the member services (entity formation, cap table management and
 *   other support services) as they are added.
 *
 * A profile is saved as a draft, submitted by an org admin with an
 * attestation, and verified (or returned with a reason) by a platform admin.
 * Whatever a type needs verified stays off until then. An organization made
 * before types existed has none, and gets none of a type's capabilities
 * until an org admin chooses one.
 *
 * Mirror of src/shared/utils/orgTypes.js, which the API enforces;
 * frontend/src/lib/orgTypes.test.js imports both to prove they agree. The
 * form labels (PROFILE_COPY) live only here.
 */

export const ORG_TYPES = ['community', 'nonprofit', 'investment'];

export const ORG_TYPE_POLICIES = Object.freeze({
  community: Object.freeze({
    label: 'Community',
    summary:
      'Education, videos, content, forums and events. Charged by transaction. No donations or investment services.',
    donations: false,
    feeModel: 'transaction',
    memberServices: false,
    profile: null,
  }),
  nonprofit: Object.freeze({
    label: 'Nonprofit',
    summary:
      'Everything in Community plus donations to your nonprofit, with the registration details, fee settings and donor disclosures charitable solicitation requires (California first).',
    donations: true,
    feeModel: 'transaction',
    memberServices: false,
    profile: 'nonprofit',
  }),
  investment: Object.freeze({
    label: 'Investment',
    summary:
      'The core tools for an investment community, with a flat fee for each service and never a share of a transaction. Members get access to services such as entity formation and cap table management. No donations.',
    donations: false,
    feeModel: 'flat-per-service',
    memberServices: true,
    profile: 'investment',
  }),
});

/** The working copy's state; `verified` means it equals the verified copy. */
export const PROFILE_STATUSES = ['draft', 'submitted', 'verified', 'returned'];

/**
 * The setup wizard's steps in order (D63 item 5). Each org sees the ones that
 * apply to its type and plan; the furthest step reached is saved so the
 * wizard can be resumed and any earlier step reopened.
 */
export const SETUP_STEP_IDS = [
  'type',
  'plan',
  'branding',
  'domain',
  'signup',
  'features',
  'profile',
  'invite',
];

export const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'PR',
];

export const IRS_STATUSES = {
  '501c3': '501(c)(3) public charity or private foundation',
  '501c-other': 'Another 501(c) organization (gifts may not be tax-deductible)',
  'fiscally-sponsored': 'A project fiscally sponsored by a 501(c)(3)',
};

export const ENTITY_TYPES = {
  llc: 'Limited liability company',
  lp: 'Limited partnership',
  corporation: 'Corporation',
  trust: 'Trust',
  other: 'Other',
};

export const OFFERING_EXEMPTIONS = {
  none: 'No securities offerings through the platform',
  '506b': 'Regulation D Rule 506(b): no general solicitation',
  '506c': 'Regulation D Rule 506(c): verified accredited investors only',
};

export const ADVISER_STATUSES = {
  none: 'Not an investment adviser',
  era: 'Exempt reporting adviser',
  ria: 'SEC-registered investment adviser',
  'state-ria': 'State-registered investment adviser',
};

const EIN_RE = /^\d{2}-\d{7}$/;
// The California Registry of Charities and Fundraisers number: CT plus digits.
const CA_REGISTRY_RE = /^CT\d{5,8}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Each profile's fields: kind, whether required, and the condition under
 * which a conditional field is required. Order is the order the forms show.
 */
export const PROFILE_FIELDS = Object.freeze({
  nonprofit: Object.freeze({
    legalName: { kind: 'text', min: 2, max: 200, required: true },
    ein: { kind: 'ein', required: true },
    irsStatus: { kind: 'choice', options: IRS_STATUSES, required: true },
    fiscalSponsorName: {
      kind: 'text',
      min: 2,
      max: 200,
      requiredWhen: p => p.irsStatus === 'fiscally-sponsored',
    },
    stateOfIncorporation: { kind: 'state', required: true },
    solicitsInCalifornia: { kind: 'boolean', required: true },
    caRegistryNumber: { kind: 'caRegistry', requiredWhen: p => p.solicitsInCalifornia === true },
    donorDisclosure: { kind: 'text', min: 40, max: 1500, required: true },
  }),
  investment: Object.freeze({
    legalName: { kind: 'text', min: 2, max: 200, required: true },
    entityType: { kind: 'choice', options: ENTITY_TYPES, required: true },
    stateOfFormation: { kind: 'state', required: true },
    offeringExemption: { kind: 'choice', options: OFFERING_EXEMPTIONS, required: true },
    adviserStatus: { kind: 'choice', options: ADVISER_STATUSES, required: true },
    complianceContactEmail: { kind: 'email', required: true },
    acknowledgesFlatFees: { kind: 'accept', required: true },
    acknowledgesAccreditedAccess: { kind: 'accept', required: true },
  }),
});

export function orgTypePolicy(orgType) {
  return ORG_TYPE_POLICIES[orgType] || null;
}

/**
 * The suggested donor disclosure for a nonprofit profile, which the org admin
 * edits and owns. Never presented as legal advice: the profile form says so.
 * The California Registry number is shown beside it, so it is not repeated.
 */
export function suggestedDonorDisclosure(profile = {}) {
  const name = profile.legalName || 'This organization';
  const ein = profile.ein ? ` (EIN ${profile.ein})` : '';
  const status =
    profile.irsStatus === '501c3'
      ? 'is a 501(c)(3) tax-exempt organization. Contributions are tax-deductible to the extent allowed by law; no goods or services are provided in exchange unless stated on your receipt.'
      : profile.irsStatus === 'fiscally-sponsored'
        ? `is a fiscally sponsored project of ${profile.fiscalSponsorName || 'its 501(c)(3) sponsor'}, which receives your gift. Contributions are tax-deductible to the extent allowed by law.`
        : 'is a tax-exempt nonprofit. Contributions are not tax-deductible as charitable contributions.';
  return `${name}${ein} ${status}`;
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeField(def, raw) {
  if (raw === undefined || raw === null || raw === '') return undefined;
  switch (def.kind) {
    case 'boolean':
    case 'accept':
      return raw;
    case 'state':
    case 'caRegistry':
      return typeof raw === 'string' ? raw.trim().toUpperCase() : raw;
    case 'email':
      return typeof raw === 'string' ? raw.trim().toLowerCase() : raw;
    default:
      return typeof raw === 'string' ? raw.trim() : raw;
  }
}

function fieldError(def, value) {
  switch (def.kind) {
    case 'text':
      if (typeof value !== 'string') return 'Enter text';
      if (value.length < def.min) return `Enter at least ${def.min} characters`;
      if (value.length > def.max) return `Keep it to ${def.max} characters`;
      return null;
    case 'ein':
      return typeof value === 'string' && EIN_RE.test(value) ? null : 'Enter the EIN as 12-3456789';
    case 'choice':
      return Object.hasOwn(def.options, value) ? null : 'Choose one of the options';
    case 'state':
      return US_STATES.includes(value) ? null : 'Choose a US state';
    case 'boolean':
      return typeof value === 'boolean' ? null : 'Choose yes or no';
    case 'accept':
      return value === true ? null : 'This must be accepted';
    case 'caRegistry':
      return typeof value === 'string' && CA_REGISTRY_RE.test(value)
        ? null
        : 'Enter the Registry number as CT followed by its digits, for example CT0123456';
    case 'email':
      return typeof value === 'string' && EMAIL_RE.test(value) && value.length <= 200
        ? null
        : 'Enter an email address';
    default:
      return 'Unknown field';
  }
}

/**
 * Validate a profile for one org type.
 *
 * `{ submit: false }` (saving a draft) checks only the fields that are filled
 * in; `{ submit: true }` also requires every required field. Returns
 * `{ value, fieldErrors }`: value holds only known fields, normalized, and
 * fieldErrors maps a field name to the message shown beside it.
 */
export function validateProfile(orgType, input, { submit = false } = {}) {
  const fields = PROFILE_FIELDS[orgTypePolicy(orgType)?.profile];
  if (!fields) return { value: {}, fieldErrors: { _: 'This organization type has no profile' } };
  if (!isPlainObject(input)) {
    return { value: {}, fieldErrors: { _: 'Send the profile as an object of fields' } };
  }
  const value = {};
  const fieldErrors = {};
  for (const key of Object.keys(input)) {
    if (!Object.hasOwn(fields, key)) fieldErrors[key] = 'Not a field of this profile';
  }
  for (const [key, def] of Object.entries(fields)) {
    const normalized = normalizeField(def, input[key]);
    if (normalized !== undefined) value[key] = normalized;
  }
  for (const [key, def] of Object.entries(fields)) {
    const required = def.required || (def.requiredWhen ? def.requiredWhen(value) : false);
    if (value[key] === undefined) {
      if (submit && required) fieldErrors[key] = 'Required';
      continue;
    }
    if (def.requiredWhen && !def.requiredWhen(value)) {
      // A conditional field that no longer applies is dropped, not checked.
      delete value[key];
      continue;
    }
    const message = fieldError(def, value[key]);
    if (message) fieldErrors[key] = message;
  }
  return { value, fieldErrors };
}

/**
 * Whether the organization has a verified profile for its type (the gate for
 * the type's capabilities). The verified copy is kept apart from the working
 * copy, so editing and resubmitting a verified profile does not switch
 * anything off while the change waits for review; the verified copy is what
 * donors see until the change is verified.
 */
export function isProfileVerified(org) {
  return Boolean(verifiedProfile(org));
}

/** The verified copy of the org's profile for its current type, or null. */
export function verifiedProfile(org) {
  const policy = orgTypePolicy(org?.orgType);
  const compliance = org?.compliance;
  if (!policy?.profile || !compliance || compliance.profileType !== policy.profile) return null;
  return compliance.verified?.profile || null;
}

/**
 * Whether the organization may take donations: a nonprofit with a verified
 * profile. A community or investment org never, and an org with no type
 * until an org admin chooses Nonprofit and the profile is verified.
 */
export function donationsAllowed(org) {
  return Boolean(orgTypePolicy(org?.orgType)?.donations) && isProfileVerified(org);
}

/** Why donations are off, in words for the org admin; null when they are allowed. */
export function donationsBlockedReason(org) {
  const policy = orgTypePolicy(org?.orgType);
  if (!policy) {
    return 'Choose your organization type in Org settings. Only nonprofit organizations can accept donations.';
  }
  if (!policy.donations) {
    return `${policy.label} organizations cannot accept donations. Only nonprofit organizations can.`;
  }
  if (!isProfileVerified(org)) {
    const status = org?.compliance?.status;
    if (status === 'submitted') {
      return 'Your nonprofit profile is waiting for platform review. Donations open once it is verified.';
    }
    if (status === 'returned') {
      return 'Your nonprofit profile was returned for changes. Update and resubmit it in Org settings.';
    }
    return 'Complete and submit your nonprofit profile in Org settings. Donations open once the platform verifies it.';
  }
  return null;
}

/**
 * Whether an org admin (not a platform admin) may change the type now: while
 * the organization has no members beyond its creator, the same setup window
 * as an address rename (D61 item 4). After that a platform admin changes it.
 */
export function orgAdminMayChangeType(org) {
  return !org?.orgType || (org?.memberCount ?? 0) <= 1;
}

/** Labels and hints for each profile field, in PROFILE_FIELDS order. */
export const PROFILE_COPY = Object.freeze({
  nonprofit: {
    legalName: {
      label: 'Legal name',
      hint: 'Exactly as registered with the IRS. Donors see it on the donate page and their receipts.',
    },
    ein: { label: 'EIN', hint: 'The federal Employer Identification Number, as 12-3456789.' },
    irsStatus: { label: 'IRS status' },
    fiscalSponsorName: {
      label: 'Fiscal sponsor',
      hint: 'The 501(c)(3) that receives gifts on your behalf.',
    },
    stateOfIncorporation: { label: 'State of incorporation' },
    solicitsInCalifornia: {
      label: 'We ask for donations from people in California',
      hint: 'California requires charities that solicit there to be registered with the Attorney General.',
    },
    caRegistryNumber: {
      label: 'California Registry of Charities and Fundraisers number',
      hint: 'Your CT number from the Attorney General Registry, for example CT0123456. It is shown to donors.',
    },
    donorDisclosure: {
      label: 'Donor disclosure',
      hint: 'Shown on your donate page and on every receipt. Say whether gifts are tax-deductible and whether donors receive anything in return. This is your statement; have your advisor review it.',
    },
  },
  investment: {
    legalName: { label: 'Legal entity name' },
    entityType: { label: 'Entity type' },
    stateOfFormation: { label: 'State of formation' },
    offeringExemption: {
      label: 'Offering exemption relied on',
      hint: 'How securities offerings, if any, are made to your members.',
    },
    adviserStatus: { label: 'Investment adviser status' },
    complianceContactEmail: {
      label: 'Compliance contact email',
      hint: 'Where the platform sends compliance questions.',
    },
    acknowledgesFlatFees: {
      label:
        'We understand the platform charges a flat fee for each service and never a share of an investment or transaction.',
    },
    acknowledgesAccreditedAccess: {
      label:
        'We will keep offering materials in areas limited to members whose accredited investor status the platform has verified.',
    },
  },
});
